<script setup lang="ts">
import { OrderBy } from '@/types/shared/filters'
import type { IEquipmentTypeFilters } from '@/types/hvl/filters'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { reactive, ref } from 'vue'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import TableSearchFilter from '@/components/common/TableSearchFilter.vue'
import { useEquipmentType } from '@/composables/hvl/equipment-type'
import UpsertEquipmentTypeDialog from '@/components/hvl/UpsertEquipmentTypeDialog.vue'
import type {
  IEquipmentType,
  IEquipmentTypeFailureType,
  IEquipmentTypeRepairAction,
  IInspectionPoint,
} from '@/types/hvl/EquipmentType'
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'

const { getEquipmentTypeQuery, deleteEquipmentTypeMutation } = useEquipmentType()

const upsertDialogVisible = ref<boolean>(false)
const equipmentTypeBeingModified = ref<IEquipmentType>()

const filters = reactive<IEquipmentTypeFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
  order_by: 'id',
  order_by_order: OrderBy.DESC,
  name: undefined,
})

const { isPending, isError, isRefetching, data, error, refetch } = getEquipmentTypeQuery(filters)

const nameSearchSettings = {
  key: 'search',
  value: 'Search',
  type: 'search',
  placeholder: 'Name',
  onChange: (val: string) => {
    if (val == '') {
      filters.name = undefined
    } else {
      filters.name = val
    }
  },
}

const onEdit = (equipmentType: IEquipmentType) => {
  equipmentTypeBeingModified.value = equipmentType
  upsertDialogVisible.value = true
}
</script>

<template>
  <ViewLayout>
    <template #controls>
      <el-button type="primary" :disabled="isPending" @click="upsertDialogVisible = true">Create</el-button>
    </template>
    <rl-error-result v-if="isError && !isRefetching" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />&nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <div class="flex inline-flex gap-4">
        <TableSearchFilter :search-settings="nameSearchSettings" :disabled="isPending" />
      </div>
      <el-table v-loading="isPending" :data="data?.data" empty-text="No data" stripe>
        <el-table-column prop="name" label="Name" :min-width="224" />
        <el-table-column label="Inspection Points" :min-width="224">
          <template #default="scope">
            <el-tag
              v-for="ip in scope.row.inspection_points.map((ip: IInspectionPoint) => ip.name)"
              :key="ip.id"
              size="small"
              class="mr-2"
            >
              {{ ip }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Failure Types" :min-width="224">
          <template #default="scope">
            <el-tag
              v-for="ft in scope.row.failure_types.map((ft: IEquipmentTypeFailureType) => ft.description)"
              :key="ft.id"
              size="small"
              class="mr-2"
            >
              {{ ft }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Repair Actions" :min-width="224">
          <template #default="scope">
            <el-tag
              v-for="ra in scope.row.repair_actions.map((ra: IEquipmentTypeRepairAction) => ra.description)"
              :key="ra.id"
              size="small"
              class="mr-2"
            >
              {{ ra }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :min-width="70" align="right">
          <template #default="scope">
            <el-button link title="Edit" @click="onEdit(scope.row as IEquipmentType)">
              <Icon icon="material-symbols:edit-outline-rounded" inline height="20" />
            </el-button>
            <el-button
              link
              title="Delete"
              @click="
                DeleteConfirmationModal(
                  'Delete Equipment Type',
                  'Are you sure you want to delete this equipment type? This action cannot be undone',
                  scope.row.id,
                  deleteEquipmentTypeMutation.mutateAsync
                )
              "
            >
              <Icon icon="material-symbols:delete-outline-rounded" inline height="20" />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="data && data.total_pages > 0"
        v-model:page-size="filters.per_page"
        v-model:current-page="filters.page"
        class="self-end"
        :page-sizes="DEFAULT_PAGE_SIZES"
        :page-count="data.total_pages"
        :total="data.total_records"
        :disabled="isPending"
        :layout="DEFAULT_PAGINATION_LAYOUT"
        background
      />
      <UpsertEquipmentTypeDialog
        v-model="upsertDialogVisible"
        :equipment-type="equipmentTypeBeingModified"
        @close="equipmentTypeBeingModified = undefined"
      />
    </template>
  </ViewLayout>
</template>
